/**
 * FeatureGateModal — UX de upgrade cuando el backend rechaza una feature con
 * 403 FEATURE_GATE_REJECTED (o cuando `useFeatureGate` la abre en pre-check).
 * Ver spec NER-3149 §5 + §9 (source of truth: services/subscriptions/gating-spec.md).
 *
 * Dos variantes (elegidas por el payload):
 *   - activate_addon: `addonAvailable && addonId` → "Activá {addon} por ${price}/mes".
 *   - upgrade_plan:   resto → "Upgrade a {plan}" + tabla comparativa de lo que desbloquea.
 *
 * El payload es el body del 403 (o el resultado de evaluateFeatureGate), con shape:
 *   { feature, requiredPlan, currentPlan, addonAvailable, addonId }
 *
 * Nota de routing (NER-3161): el backend manda `upgradeUrl: '/admin/billing/upgrade'`,
 * pero esa ruta NO existe en esta app. Las rutas reales son `/plan-selection` (upgrade)
 * y `/subscription` (add-ons, sección AddonsSection). Navegamos a esas, no al upgradeUrl.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../context/SubscriptionContext';
import { getAddons } from '../services/api';
import { formatPriceWithCurrency } from '../utils/formatCurrency';
import {
  canonicalPlan,
  toFeatureBase,
  toFeatureFlag,
  featuresUnlockedBy,
} from '../config/featureGates';
import './FeatureGateModal.css';

const LOCALE_CURRENCY = { es: 'ARS', en: 'USD', pt: 'BRL' };

function FeatureGateModal({ gate, onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const [addon, setAddon] = useState(null);

  const flag = toFeatureFlag(gate?.feature);
  const base = toFeatureBase(flag);
  const currentPlan = canonicalPlan(gate?.currentPlan || subscription?.planId || 'free');
  const requiredPlan = canonicalPlan(gate?.requiredPlan || 'professional');
  const isAddon = Boolean(gate?.addonAvailable && gate?.addonId);

  const currency =
    subscription?.currency ||
    subscription?.pricing?.currency ||
    LOCALE_CURRENCY[i18n.language] ||
    'ARS';

  // Nombre legible de la feature y del plan (fallback al identificador crudo).
  const featureName = t(`featureGate.features.${base}`, base);
  const planName = t(`planSelection.plans.${requiredPlan}.name`, requiredPlan);

  // Para la variante add-on: resolvemos nombre + precio reales del catálogo backend.
  useEffect(() => {
    let cancelled = false;
    if (!isAddon) {
      setAddon(null);
      return undefined;
    }
    getAddons(currentPlan)
      .then((result) => {
        if (cancelled) return;
        const list = result?.data || [];
        setAddon(list.find((a) => a.addOnId === gate.addonId) || null);
      })
      .catch(() => {
        if (!cancelled) setAddon(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAddon, currentPlan, gate?.addonId]);

  // Escape para cerrar (accesibilidad).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const goUpgrade = () => {
    onClose();
    navigate('/plan-selection', { state: { currentSubscription: subscription } });
  };

  const goAddons = () => {
    onClose();
    navigate('/subscription');
  };

  const addonName = addon?.name || t(`featureGate.addons.${gate?.addonId}`, gate?.addonId || '');
  const addonPrice = addon?.pricing?.[currency]?.monthly;
  const addonPriceLabel =
    addonPrice != null
      ? `${formatPriceWithCurrency(addonPrice, currency)}/${t('planSelection.month')}`
      : null;

  const unlocked = featuresUnlockedBy(requiredPlan, currentPlan);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content feature-gate-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feature-gate-title"
      >
        <div className="modal-header">
          <h2 id="feature-gate-title">
            {isAddon ? t('featureGate.addonTitle') : t('featureGate.upgradeTitle')}
          </h2>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label={t('common.cancel')}
          >
            &times;
          </button>
        </div>

        <div className="modal-body">
          {isAddon ? (
            <>
              <p>
                {addonPriceLabel
                  ? t('featureGate.addonMessage', {
                      addon: addonName,
                      price: addonPriceLabel,
                      feature: featureName,
                    })
                  : t('featureGate.addonMessageNoPrice', {
                      addon: addonName,
                      feature: featureName,
                    })}
              </p>
              <p className="feature-gate-secondary">{t('featureGate.addonHint')}</p>
            </>
          ) : (
            <>
              <p>
                {t('featureGate.upgradeMessage', {
                  plan: planName,
                  feature: featureName,
                })}
              </p>
              {unlocked.length > 0 && (
                <div
                  className="feature-gate-table"
                  role="table"
                  aria-label={t('featureGate.unlockCaption')}
                >
                  <div className="feature-gate-table-caption">{t('featureGate.unlockCaption')}</div>
                  <div className="feature-gate-row feature-gate-row--head" role="row">
                    <span role="columnheader">{t('featureGate.colFeature')}</span>
                    <span role="columnheader">
                      {t(`planSelection.plans.${currentPlan}.name`, currentPlan)}
                    </span>
                    <span role="columnheader">{planName}</span>
                  </div>
                  {unlocked.map((f) => {
                    const fbase = toFeatureBase(f);
                    const isTarget = f === flag;
                    return (
                      <div
                        key={f}
                        className={`feature-gate-row${isTarget ? ' feature-gate-row--target' : ''}`}
                        role="row"
                      >
                        <span role="cell">{t(`featureGate.features.${fbase}`, fbase)}</span>
                        <span
                          role="cell"
                          className="feature-gate-no"
                          aria-label={t('featureGate.notIncluded')}
                        >
                          —
                        </span>
                        <span
                          role="cell"
                          className="feature-gate-yes"
                          aria-label={t('featureGate.included')}
                        >
                          ✓
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          {isAddon ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={goUpgrade}>
                {t('featureGate.upgradeInstead', { plan: planName })}
              </button>
              <button type="button" className="btn btn-primary" onClick={goAddons}>
                {t('featureGate.activateAddonCta')}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary" onClick={goUpgrade}>
              {t('featureGate.upgradeCta', { plan: planName })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default FeatureGateModal;
